import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";

import axios, { AxiosError } from "axios";
import { selectTxnId, useMapStore } from "stores/map";
import { useDebouncedCallback } from "use-debounce";

import { MAP_SERVICE_ADDRESS } from "../../../constants/app.constants";
import { CoverageType } from "../toolbar/MapVis";
import { emptyFeatureCollection } from "../utils/emptyFeatureCollection";

const API_URL = `${MAP_SERVICE_ADDRESS}/map-vis/coverage`;

export const GetMapCoverageKey = "mapCoverage";

// Gates the coverage request so a burst of txn/option changes settles first.
// Every input here is discrete — the radius input commits on blur, not while
// dragging — so this only needs to be long enough to coalesce a burst, and a
// longer wait is pure delay before the layer catches up with a colour change.
export const COVERAGE_FETCH_DEBOUNCE_MS = 500;

const fetchCoverage = async (
  txnId: string,
  show: boolean,
  size: number,
  coverageType: CoverageType
) => {
  if (!txnId || !show) return Promise.resolve({ ...emptyFeatureCollection });
  const response = await axios.get<GeoJSON.FeatureCollection>(
    `${API_URL}/${txnId}?size=${size}&coverageType=${coverageType}`
  );
  return response.data;
};

const useGetGeoMapSettings = (coverageOptions: {
  showCoverage: boolean;
  radius: number;
  coverageType: CoverageType;
}) => {
  const txnId = useMapStore(selectTxnId);
  const { radius, coverageType, showCoverage } = coverageOptions;
  const [enabled, setEnabled] = useState(false);
  const debouncedFetch = useDebouncedCallback(() => {
    if (!txnId?.id || !coverageOptions.showCoverage || !coverageOptions.coverageType)
      return;
    setEnabled(true);
  }, COVERAGE_FETCH_DEBOUNCE_MS);
  useEffect(() => {
    if (!coverageOptions.showCoverage || !txnId?.id) {
      return;
    }
    debouncedFetch();
  }, [txnId, coverageOptions]);
  return useQuery<GeoJSON.FeatureCollection, AxiosError>(
    // txnId.id is part of the key so a new transaction (e.g. a colour-lock re-bin)
    // can't be served stale coverage geojson cached under the old txn.
    [GetMapCoverageKey, txnId?.id, radius, coverageType],
    () => fetchCoverage(txnId.id, showCoverage, radius, coverageType),
    {
      refetchOnWindowFocus: false,
      enabled: enabled,
      // onSettled, not onSuccess: closing the gate only on success meant one
      // failed coverage fetch left `enabled` true for the rest of the session,
      // so every later txn/radius/shape change fetched immediately and the
      // debounce above stopped applying at all.
      onSettled: () => {
        setEnabled(false);
      },
      onError: (error) => {
        Sentry.withScope((scope) => {
          scope.setContext("map_coverage_fetch", {
            txnId: txnId?.id,
            radius,
            coverageType
          });
          scope.setLevel("error");
          Sentry.captureException(error);
        });
      }
    }
  );
};

export default useGetGeoMapSettings;
